// The numberlike grammar for RMS `number` attributes (#10991), mirrored from
// the backend's `fp/rms/numberlike.py`, which is the authority on what is
// stored. For an entity fact the frontend never parses for storage: it sends
// the typed text and renders what the server returns. This mirror exists for
// the things that have to happen before the round trip -- the editor's live
// highlight of the leading run and deciding whether a paste can land in a
// number cell -- and for the copy and export text of a stored fact. The one
// place it does decide what is stored is the custom-cell stores (holdings,
// transactions, document and interaction cells), which take a bare number and
// never learned the grammar: `Search/TablePanel/persistence.ts` reduces the
// text to its value before posting there.
//
// The first number is the value; the words around it are the note. The run
// starts at the first number in the text and continues through numbers joined
// by `,` `to` `-` `–` `/` `or` `and`; it ends at the first word that is neither
// a number nor a joiner. One number is a point value; two or more make a range
// from the smallest to the largest. Text with no number at all is refused.
// Position decides, never a guess about which number in the text is the
// value: `the fee is 20% over an 8% hurdle` can only mean 20%.

// Where the figure sits inside a note with words before it: `nearly $500M`
// stores the note `nearly ⟨n⟩`, and every renderer puts the figure back where
// the placeholder is. A note without one reads after the figure.
export const FIGURE_PLACEHOLDER = "⟨n⟩";

// The number units that hold US dollars. `usd` displays compact ("$10.0M")
// and `usd_full` displays the whole figure ("$10,000,232"); parsing, editing,
// copying, filtering and export treat them alike. Mirrors `is_dollar_unit` in
// the backend's `fp/rms/numberlike.py`.
export const isDollarUnit = (unit: string | null | undefined): boolean =>
  unit === "usd" || unit === "usd_full";

// The number units that show a plain figure with its thousands grouped and
// no currency sign or magnitude suffix (#12942): `grouped` as a whole number
// ("1,234,568"), `grouped_2dp` with exactly two decimals ("1,234,567.50").
// Parsing treats them as a unit-less number. Mirrors `is_grouped_unit` in the
// backend's `fp/rms/numberlike.py`.
type GroupedUnit = "grouped" | "grouped_2dp";

const GROUPED_DECIMALS: Record<GroupedUnit, number> = {
  grouped: 0,
  grouped_2dp: 2,
};

export const isGroupedUnit = (
  unit: string | null | undefined
): unit is GroupedUnit => unit === "grouped" || unit === "grouped_2dp";

// A number on a grouped unit as every surface shows it: en-US comma grouping
// and the unit's fixed decimals, rounded half away from zero. The record
// page, the identity strip and the copy text all render through this, so
// they never disagree; the backend's `format_grouped` writes the export.
export const formatGrouped = (n: number, unit: GroupedUnit): string => {
  const decimals = GROUPED_DECIMALS[unit];
  const text = n.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  // A small negative rounds to "-0"; zero carries no sign.
  return /^-0(\.0+)?$/.test(text) ? text.slice(1) : text;
};

// The number unit of a human percentage (#12500): the stored figure is the
// percentage itself (6.2 is 6.2%), never scaled, and every surface shows and
// copies it with two decimals and a "%" sign. Holdings and transactions
// columns of type `percentage` carry it. Distinct from `percent`, which
// stores a decimal fraction (0.062 is 6.2%). Mirrors `PERCENT_POINTS` in the
// backend's `fp/rms/numberlike.py`.
export const PERCENT_POINTS = "percent_points";

// A human percentage as the grid, the record page, the copy text and the
// export all write it: the figure as `grouped_2dp` shows it, then "%".
export const formatPercentPoints = (n: number): string =>
  `${formatGrouped(n, "grouped_2dp")}%`;

export interface NumberlikeParse {
  // The point value, or the low end of a range. On a percent attribute the
  // stored fraction (1.25% is 0.0125); on a percent_points attribute the
  // points themselves (1.25% is 1.25).
  value: number;
  // The high end of a range; null for a point value.
  end: number | null;
  // The words before and after the run; null when there are none.
  note: string | null;
  // Where the run starts and ends in the input, for the editor's highlight.
  runStart: number;
  runEnd: number;
}

const SUFFIX_MULTIPLIERS: Record<string, number> = {
  k: 1_000,
  thousand: 1_000,
  m: 1_000_000,
  mm: 1_000_000,
  million: 1_000_000,
  b: 1_000_000_000,
  bn: 1_000_000_000,
  billion: 1_000_000_000,
  t: 1_000_000_000_000,
  trillion: 1_000_000_000_000,
};

// One number token: optional sign and dollar sign, digits with optional
// thousands grouping and decimals, an optional magnitude suffix that must be
// a whole word (the m of "months" is not a million), and an optional percent
// or basis-point marker. Sticky, so it matches at `lastIndex` only.
// The digit run may not end mid-run (`(?![0-9])`): without that, `50bps`
// reads as `5` with the note `0bps`. The percent marker is matched
// independently of the suffix so `50bps` and `50 bps` read alike. An exponent
// (`1E+09`, a spreadsheet's spelling of a large figure) is part of the number.
// Basis points spelled out count as the marker.
const TOKEN =
  /(-)?\$?\s*(-)?((?:(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?|\.\d+)(?:[eE][-+]?\d+)?)(?![0-9])\s*(?:(thousand|million|billion|trillion|mm|bn|k|m|b|t)(?![a-z]))?\s*(%|(?:bps|bp|basis\s*points?)(?![a-z]))?/iy;

// What may sit between two numbers of one run.
// One or more joiners may sit between two numbers: "1.375%, or 1.5%" is a
// comma and an "or", and the run continues through both.
const JOINER = /\s*(?:(?:,|\/|-|–|—|\bto\b|\bor\b|\band\b)\s*)+/iy;

const tokenValue = (
  match: RegExpExecArray,
  unit: string | null | undefined,
  inheritedSuffix: string | undefined,
  inheritedMarker: string | undefined,
  cap: number | undefined
): number => {
  let number = parseFloat(match[3].replace(/,/g, ""));
  if (match[1] || match[2]) number = -number;
  let suffix: string | undefined = match[4];
  let marker: string | undefined = match[5];
  if (suffix === undefined && marker === undefined) {
    marker = inheritedMarker;
    if (inheritedSuffix !== undefined) {
      const scaled = number * SUFFIX_MULTIPLIERS[inheritedSuffix.toLowerCase()];
      if (cap === undefined || Math.abs(scaled) <= Math.abs(cap)) {
        suffix = inheritedSuffix;
      }
    }
  }
  if (suffix) number *= SUFFIX_MULTIPLIERS[suffix.toLowerCase()];
  if (unit === "percent") {
    const isBps = marker !== undefined && marker !== "%";
    number = number / (isBps ? 10_000 : 100);
    number = Number(number.toPrecision(10));
  } else if (
    unit === PERCENT_POINTS &&
    marker !== undefined &&
    marker !== "%"
  ) {
    // On a human percentage the figure is the points: `%` changes nothing,
    // and basis points are hundredths of a point.
    number = Number((number / 100).toPrecision(10));
  }
  return number;
};

// The sticky token, searched from any position: the run starts at the first
// number in the text.
const findFirstToken = (raw: string): RegExpExecArray | null => {
  for (let i = 0; i < raw.length; i++) {
    TOKEN.lastIndex = i;
    const match = TOKEN.exec(raw);
    if (match !== null) return match;
  }
  return null;
};

// Parse text under the grammar, or null when it holds no number at all.
export function parseNumberlike(
  raw: string,
  unit: string | null | undefined
): NumberlikeParse | null {
  let match = findFirstToken(raw);
  if (match === null) return null;
  // The token admits a space between a `$` and its digits, so its match can
  // begin on whitespace; the run itself starts at the first ink.
  const start = match.index + (match[0].match(/^\s*/)?.[0].length ?? 0);
  const tokens: RegExpExecArray[] = [];
  let pos = start;
  while (match !== null) {
    tokens.push(match);
    pos = TOKEN.lastIndex;
    JOINER.lastIndex = pos;
    const joiner = JOINER.exec(raw);
    if (joiner === null) break;
    TOKEN.lastIndex = JOINER.lastIndex;
    match = TOKEN.exec(raw);
  }
  // A marker or magnitude on the last number of a run is written once for
  // all of them (`475 to 500 bps`, `1.5 to 2B`); an earlier number that
  // carries neither inherits the last one's, the magnitude only while it
  // keeps that number below the last one (`900 or 2.9B` stays 900).
  const last = tokens[tokens.length - 1];
  const lastValue = tokenValue(last, unit, undefined, undefined, undefined);
  const numbers = tokens
    .slice(0, -1)
    .map((token) => tokenValue(token, unit, last[4], last[5], lastValue))
    .concat([lastValue]);
  const before = raw
    .slice(0, start)
    .trim()
    .replace(/[,;:]+$/, "")
    .trim();
  const after = raw
    .slice(pos)
    .trim()
    .replace(/^[,;:.\-–—/]+/, "")
    .trim();
  const note =
    before !== ""
      ? [before, FIGURE_PLACEHOLDER, after]
          .filter((part) => part !== "")
          .join(" ")
      : after || null;
  const low = Math.min(...numbers);
  const high = Math.max(...numbers);
  return {
    value: low,
    end: high === low ? null : high,
    note,
    runStart: start,
    runEnd: pos,
  };
}

// A number in the editor's spelling: the figure in its unit with no more
// digits than it needs, so the text round-trips through the parser.
const editorPoint = (n: number, unit: string | null | undefined): string => {
  const trimmed = (x: number): string => Number(x.toPrecision(10)).toString();
  if (unit === "percent") return `${trimmed(n * 100)}%`;
  if (unit === PERCENT_POINTS) return `${trimmed(n)}%`;
  if (isDollarUnit(unit)) {
    return `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString("en-US", {
      maximumFractionDigits: 10,
    })}`;
  }
  if (isGroupedUnit(unit)) {
    return n.toLocaleString("en-US", { maximumFractionDigits: 10 });
  }
  return trimmed(n);
};

// The figure and its note as one line, the figure standing where the note's
// placeholder is, or first when the note has none.
export function placeFigure(
  figure: string,
  note: string | null | undefined
): string {
  if (!note) return figure;
  if (note.includes(FIGURE_PLACEHOLDER)) {
    return note
      .split(FIGURE_PLACEHOLDER)
      .join(figure)
      .replace(/\s+/g, " ")
      .trim();
  }
  return `${figure} ${note}`;
}

// The note's two halves around the figure, for a renderer that styles the
// figure and the words differently.
export function splitNote(note: string | null | undefined): {
  before: string | null;
  after: string | null;
} {
  if (!note) return { before: null, after: null };
  const at = note.indexOf(FIGURE_PLACEHOLDER);
  if (at < 0) return { before: null, after: note };
  const before = note.slice(0, at).trim();
  const after = note.slice(at + FIGURE_PLACEHOLDER.length).trim();
  return { before: before || null, after: after || null };
}

// The text the editor opens with for a stored number fact.
export function numberlikeEditorText(
  value: number,
  end: number | null | undefined,
  note: string | null | undefined,
  unit: string | null | undefined
): string {
  let figure = editorPoint(value, unit);
  if (end !== null && end !== undefined) {
    figure = `${figure} – ${editorPoint(end, unit)}`;
  }
  return placeFigure(figure, note);
}

// A number as the grid's copy text and the export write it, matching the
// server-side CSV export: two decimals for dollars and both percent units,
// and a grouped unit's own decimals.
const copyPoint = (n: number, unit: string | null | undefined): string => {
  if (isDollarUnit(unit)) {
    return `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  if (unit === "percent") {
    return `${(n * 100).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}%`;
  }
  if (unit === PERCENT_POINTS) return formatPercentPoints(n);
  if (unit === "amount") {
    return n.toLocaleString("en-US", { maximumFractionDigits: 20 });
  }
  if (isGroupedUnit(unit)) return formatGrouped(n, unit);
  return String(n);
};

// The copy / export text of a stored number fact: the point or range, then
// the note. A point with no note reads exactly as a number always has.
export function numberlikeCopyText(
  value: number | string,
  end: number | null | undefined,
  note: string | null | undefined,
  unit: string | null | undefined
): string {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  let figure = copyPoint(n, unit);
  if (end !== null && end !== undefined) {
    figure = `${figure} – ${copyPoint(end, unit)}`;
  }
  return placeFigure(figure, note);
}
